import { clamp } from '../core/math';
import type { World } from '../sim/world';

// ---------------------------------------------------------------------------
// Notes and the title tune
// ---------------------------------------------------------------------------

const SEMITONE: Record<string, number> = {
  C: 0, 'C#': 1, D: 2, 'D#': 3, E: 4, F: 5, 'F#': 6, G: 7, 'G#': 8, A: 9, 'A#': 10, B: 11,
};

/** "A4", "G#3" -> Hz. Returns 0 for a rest. */
export function freq(name: string): number {
  const m = /^([A-G]#?)(-?\d)$/.exec(name);
  if (!m) return 0;
  const midi = SEMITONE[m[1]] + (Number(m[2]) + 1) * 12;
  return 440 * Math.pow(2, (midi - 69) / 12);
}

/**
 * The title tune is an Andalusian cadence: the four chords Am, G, F, E, which
 * are the sound of Andalusia, the region of southern Spain the game is set in.
 * They are played on square waves so it sounds like a 1983 arcade machine.
 * One token per eighth note; '.' is a rest.
 */
const MELODY = (
  'A4 C5 E5 C5 A4 B4 C5 A4 ' +
  'G4 B4 D5 B4 G4 A4 B4 G4 ' +
  'F4 A4 C5 A4 F4 G4 A4 F4 ' +
  'E4 G#4 B4 G#4 E4 F4 E4 .'
).split(/\s+/);

const HARMONY = (
  'E4 . A4 . E4 . A4 . ' +
  'D4 . G4 . D4 . G4 . ' +
  'C4 . F4 . C4 . F4 . ' +
  'B3 . E4 . B3 . G#4 .'
).split(/\s+/);

const BASS = (
  'A2 . A2 A2 A2 . E2 . ' +
  'G2 . G2 G2 G2 . D2 . ' +
  'F2 . F2 F2 F2 . C2 . ' +
  'E2 . E2 E2 E2 . B1 .'
).split(/\s+/);

/** K = kick, H = snare, h = hat. */
const DRUMS = ('K h H h K h H h ' + 'K h H h K h H h ' + 'K h H h K h H h ' + 'K h H h K h H K').split(/\s+/);

const BPM = 138;
const EIGHTH = 30 / BPM;

/** Exposed so the tune data can be checked for typos. */
export const TUNE = { MELODY, HARMONY, BASS, DRUMS, BPM, EIGHTH };

// Bed levels for the attract screen, calibrated by measuring the output of each
// one on its own. They are large because the filters in front of them discard
// most of the noise's amplitude first.
const TITLE_FIRE = 0.85;
const TITLE_WIND = 0.42;
const TITLE_ROTOR = 1.15;

/**
 * Everything is synthesised, so there are no asset files. A looping bed of
 * noise for the rotor, fire and wind, plus a handful of one-shot sounds.
 */
export class Sound {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private rotor: GainNode | null = null;
  private rotorChop: GainNode | null = null;
  private fire: GainNode | null = null;
  private fireFilter: BiquadFilterNode | null = null;
  private wind: GainNode | null = null;
  private windFilter: BiquadFilterNode | null = null;
  /** Blade passes per second, and when the next one is due. */
  private bladeHz = 8.5;
  private bladeAt = 0;
  private noise: AudioBuffer | null = null;
  /** Crackle spits per second, and how loud each one is. */
  private crackleRate = 0;
  private crackleLevel = 0.5;
  private crackleTimer: ReturnType<typeof setInterval> | null = null;
  /** True while a radio transmission is on air, so calls do not overlap. */
  private radioBusy = false;
  private voices: SpeechSynthesisVoice[] = [];
  muted = false;
  started = false;

  start(): void {
    if (this.started) return;
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    this.started = true;
    const ctx = new Ctor();
    this.ctx = ctx;

    // Two seconds of white noise, reused by every bed.
    const buf = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    this.noise = buf;

    // A limiter on the end, so a tune peak landing on a blade slap and a
    // crackle at the same instant can never come out as a click.
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -6;
    limiter.knee.value = 3;
    limiter.ratio.value = 12;
    limiter.attack.value = 0.003;
    limiter.release.value = 0.12;
    limiter.connect(ctx.destination);

    this.master = ctx.createGain();
    this.master.gain.value = 0.5;
    this.master.connect(limiter);

    // NOTE ON LEVELS: every bed is heavily filtered, and a filter throws away
    // most of white noise's amplitude before it reaches the mixer. A narrow
    // band keeps only a few percent of the power. The gains below therefore
    // look large; they are calibrated against measured output, not guessed.

    // Rotor: low-passed noise chopped right down to silence between blades, so
    // you hear separate beats rather than a continuous hiss.
    const rSrc = ctx.createBufferSource();
    rSrc.buffer = buf;
    rSrc.loop = true;
    const rLow = ctx.createBiquadFilter();
    rLow.type = 'lowpass';
    rLow.frequency.value = 950;
    rLow.Q.value = 0.9;
    this.rotorChop = ctx.createGain();
    this.rotorChop.gain.value = 0.08;
    this.rotor = ctx.createGain();
    this.rotor.gain.value = 0;
    rSrc.connect(rLow).connect(this.rotorChop).connect(this.rotor).connect(this.master);
    rSrc.start();
    // Blades are scheduled one at a time, not driven by an oscillator. A slow
    // oscillator on the gain can only fade the sound in and out, and what you
    // want to hear is the sharp slap of each blade.
    this.bladeAt = ctx.currentTime + 0.05;
    this.pumpRotor();

    // Fire: a low roar. The crackle itself is separate; see cracklePop().
    const fSrc = ctx.createBufferSource();
    fSrc.buffer = buf;
    fSrc.loop = true;
    this.fireFilter = ctx.createBiquadFilter();
    this.fireFilter.type = 'lowpass';
    this.fireFilter.frequency.value = 620;
    this.fire = ctx.createGain();
    this.fire.gain.value = 0;
    fSrc.connect(this.fireFilter).connect(this.fire).connect(this.master);
    fSrc.start();

    // Wind: a broad band whose centre frequency keeps moving up and down, so
    // it rushes rather than hisses.
    const wSrc = ctx.createBufferSource();
    wSrc.buffer = buf;
    wSrc.loop = true;
    this.windFilter = ctx.createBiquadFilter();
    this.windFilter.type = 'bandpass';
    this.windFilter.frequency.value = 700;
    this.windFilter.Q.value = 0.45;
    const gust = ctx.createOscillator();
    gust.type = 'sine';
    gust.frequency.value = 0.17;
    const gustGain = ctx.createGain();
    gustGain.gain.value = 320;
    gust.connect(gustGain).connect(this.windFilter.frequency);
    this.wind = ctx.createGain();
    this.wind.gain.value = 0;
    wSrc.connect(this.windFilter).connect(this.wind).connect(this.master);
    wSrc.start();
    gust.start();

    // One timer drives the crackle for the whole game; rate is set elsewhere.
    this.crackleTimer = setInterval(() => this.cracklePop(), 55);

    // Speech voices load asynchronously and getVoices() is usually empty on the
    // first call, so cache them as they arrive.
    const synth = typeof window !== 'undefined' ? window.speechSynthesis : undefined;
    if (synth) {
      const grab = () => {
        this.voices = synth.getVoices();
      };
      grab();
      synth.addEventListener?.('voiceschanged', grab);
      setTimeout(grab, 400);
      setTimeout(grab, 1500);
    }
  }

  /**
   * Each blade is a sharp slap decaying almost to silence before the next one,
   * scheduled ahead like the tune. This is what makes it a helicopter rather
   * than a hiss that gets slightly louder and quieter.
   */
  private pumpRotor(): void {
    const ctx = this.ctx;
    const chop = this.rotorChop;
    if (!ctx || !chop) return;
    const period = 1 / this.bladeHz;
    while (this.bladeAt < ctx.currentTime + 0.9) {
      const t = Math.max(this.bladeAt, ctx.currentTime + 0.01);
      chop.gain.setValueAtTime(0.07, t);
      chop.gain.linearRampToValueAtTime(1, t + 0.012);
      chop.gain.exponentialRampToValueAtTime(0.07, t + period * 0.8);
      this.bladeAt += period;
    }
    setTimeout(() => this.pumpRotor(), 300);
  }

  /**
   * A single spit from the fire. Real crackle is made of many tiny, very short
   * sounds, not a hiss, so the bed alone never sounded like burning.
   */
  private cracklePop(): void {
    const ctx = this.ctx;
    if (!ctx || !this.noise || !this.master || this.crackleRate <= 0) return;
    if (Math.random() > this.crackleRate * 0.055) return;
    const t0 = ctx.currentTime;
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    src.playbackRate.value = 1.4 + Math.random() * 1.2;
    const band = ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.frequency.value = 1100 + Math.random() * 2600;
    band.Q.value = 1.6;
    const g = ctx.createGain();
    const dur = 0.012 + Math.random() * 0.03;
    const peak = (0.55 + Math.random() * 0.9) * this.crackleLevel;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(peak, t0 + 0.002);
    g.gain.exponentialRampToValueAtTime(0.0004, t0 + dur);
    src.connect(band).connect(g).connect(this.master);
    src.start(t0);
    src.stop(t0 + dur + 0.02);
  }

  /** Stop the timers. Not needed in the page, but tests need no timer left running. */
  dispose(): void {
    if (this.crackleTimer !== null) clearInterval(this.crackleTimer);
    if (this.crowdTimer !== null) clearInterval(this.crowdTimer);
    if (this.tuneTimer !== null) clearTimeout(this.tuneTimer);
    this.crackleTimer = null;
    this.crowdTimer = null;
    this.tuneTimer = null;
  }

  setMuted(m: boolean): void {
    this.muted = m;
    if (this.master && this.ctx) this.master.gain.setTargetAtTime(m ? 0 : 0.5, this.ctx.currentTime, 0.05);
  }

  /** True while the browser is still blocking audio until the user clicks or types. */
  get blocked(): boolean {
    return !this.ctx || this.ctx.state !== 'running';
  }

  /** Called on the first real interaction; harmless if already running. */
  resume(): void {
    if (this.ctx && this.ctx.state !== 'running') void this.ctx.resume();
  }

  // -------------------------------------------------------------------------
  // Title-screen atmosphere
  // -------------------------------------------------------------------------

  private titleMode = false;
  private crowdTimer: ReturnType<typeof setInterval> | null = null;

  /**
   * The attract screen (the demo that runs before a game starts) has its own
   * set of sounds rather than those of the simulation running unseen behind
   * it: fire crackling, wind over the ridge, a helicopter holding overhead,
   * and the rescued villagers cheering every few bars.
   */
  titleAmbience(on: boolean): void {
    if (on === this.titleMode) return;
    this.titleMode = on;
    const ctx = this.ctx;
    if (!ctx || !this.fire || !this.wind || !this.rotor || !this.rotorChop || !this.fireFilter) return;
    const t = ctx.currentTime;
    if (on) {
      this.fire.gain.setTargetAtTime(TITLE_FIRE, t, 0.8);
      this.fireFilter.frequency.setTargetAtTime(720, t, 0.8);
      this.crackleRate = 13;
      this.crackleLevel = 0.42;
      this.wind.gain.setTargetAtTime(TITLE_WIND, t, 1.0);
      // The rotor is not a steady bed here. titleCue() fades it in and out.
      this.rotor.gain.cancelScheduledValues(t);
      this.rotor.gain.setValueAtTime(0.0001, t);
      this.bladeHz = 7.4;
      // Every two loops of the tune: the whole sequence needs about fourteen
      // seconds now that the radio call and a proper long cheer are in it.
      setTimeout(() => this.titleCue(), 500);
      this.crowdTimer = setInterval(() => this.titleCue(), 6960 * 2);
    } else {
      if (this.crowdTimer !== null) clearInterval(this.crowdTimer);
      this.crowdTimer = null;
      this.crackleRate = 0;
      // Cancel the cue's pending ramps, or they will still be changing the
      // gains while update() sets them during play.
      this.fire.gain.cancelScheduledValues(t);
      this.rotor.gain.cancelScheduledValues(t);
      this.fire.gain.setTargetAtTime(0, t, 0.4);
      this.wind.gain.setTargetAtTime(0, t, 0.4);
      this.rotor.gain.setTargetAtTime(0, t, 0.3);
    }
  }

  /**
   * The sequence the attract screen plays, once per loop of the tune. The
   * helicopter has to leave before the village can be heard cheering, because
   * with the rotor overhead and the fire at full volume, a crowd cannot be
   * heard over them.
   *
   *   0.0s  distant rotor, approaching
   *   2.4s  overhead, the bucket opens
   *   3.0s  water hits the fire; the fire drops back
   *   3.7s  the helicopter pulls away
   *   5.9s  clear air, and the village cheers
   *   6.6s  fire builds again for the next pass
   */
  private titleCue(): void {
    const ctx = this.ctx;
    const rotor = this.rotor;
    const fire = this.fire;
    if (!this.titleMode || !ctx || !rotor || !fire) return;

    const later = (ms: number, fn: () => void) =>
      setTimeout(() => {
        if (this.titleMode) fn();
      }, ms);

    // Coming in from a long way off.
    const t0 = ctx.currentTime;
    rotor.gain.cancelScheduledValues(t0);
    rotor.gain.setValueAtTime(0.0001, t0);
    rotor.gain.exponentialRampToValueAtTime(TITLE_ROTOR, t0 + 2.3);
    this.bladeHz = 7.4;
    later(1500, () => {
      this.bladeHz = 9.6;
    });

    later(2400, () => this.whoosh());

    later(3000, () => {
      this.steam();
      const t = ctx.currentTime;
      fire.gain.cancelScheduledValues(t);
      fire.gain.setValueAtTime(Math.max(0.0002, fire.gain.value), t);
      fire.gain.exponentialRampToValueAtTime(TITLE_FIRE * 0.1, t + 1.3);
      this.crackleRate = 2;
    });

    // Away again, so the air is clear for the cheer.
    later(3700, () => {
      const t = ctx.currentTime;
      rotor.gain.cancelScheduledValues(t);
      rotor.gain.setValueAtTime(Math.max(0.0002, rotor.gain.value), t);
      rotor.gain.exponentialRampToValueAtTime(0.0001, t + 2);
      this.bladeHz = 7.2;
    });

    // The commander calls it in before anyone celebrates.
    later(4500, () => this.radioCall('Fire extinguished. Over.'));

    later(7300, () => this.crowd());

    later(12600, () => {
      const t = ctx.currentTime;
      fire.gain.cancelScheduledValues(t);
      fire.gain.setValueAtTime(Math.max(0.0002, fire.gain.value), t);
      fire.gain.exponentialRampToValueAtTime(TITLE_FIRE, t + 1.4);
      this.crackleRate = 13;
    });
  }

  /** The click of a radio's press-to-talk key, opening or closing. */
  private squelch(at: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.noise || !this.master) return;
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    src.playbackRate.value = 2.2;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 1900;
    bp.Q.value = 1.2;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, at);
    g.gain.exponentialRampToValueAtTime(0.5, at + 0.006);
    g.gain.exponentialRampToValueAtTime(0.0006, at + 0.09);
    src.connect(bp).connect(g).connect(this.master);
    src.start(at);
    src.stop(at + 0.12);
  }

  /**
   * A radio call from the helicopter. The words come from the browser's own
   * speech synthesis, which is the only way to get speech without shipping an
   * audio file. Everything else drops in volume underneath it so the words
   * stay clear. The speech does not pass through our audio graph, so lowering
   * the master gain is exactly what we want: it turns the game down and leaves
   * the voice alone.
   */
  radioCall(text: string): void {
    const ctx = this.ctx;
    if (!ctx || !this.master || this.radioBusy) return;
    this.radioBusy = true;
    const t0 = ctx.currentTime;

    this.squelch(t0);
    // The hiss of an open radio channel, for the length of the transmission.
    if (this.noise) {
      const hiss = ctx.createBufferSource();
      hiss.buffer = this.noise;
      hiss.loop = true;
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = 1800;
      bp.Q.value = 0.7;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(0.5, t0 + 0.05);
      g.gain.setValueAtTime(0.5, t0 + 2.2);
      g.gain.exponentialRampToValueAtTime(0.0006, t0 + 2.5);
      hiss.connect(bp).connect(g).connect(this.master);
      hiss.start(t0);
      hiss.stop(t0 + 2.6);
    }

    // Turn the music and the hillside sounds down under the transmission.
    if (!this.muted) {
      this.master.gain.cancelScheduledValues(t0);
      this.master.gain.setValueAtTime(this.master.gain.value, t0);
      this.master.gain.linearRampToValueAtTime(0.16, t0 + 0.12);
      this.master.gain.setValueAtTime(0.16, t0 + 2.1);
      this.master.gain.linearRampToValueAtTime(0.5, t0 + 2.6);
    }

    const done = () => {
      if (this.ctx) this.squelch(this.ctx.currentTime);
      this.radioBusy = false;
    };

    const synth = typeof window !== 'undefined' ? window.speechSynthesis : undefined;
    if (!synth) {
      setTimeout(done, 2100);
      return;
    }
    setTimeout(() => {
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 1.05;
      u.pitch = 0.75;
      u.volume = 1;
      const voices = this.voices.length ? this.voices : synth.getVoices();
      const pick =
        voices.find((v) => /en-GB/i.test(v.lang) && /male|daniel|george|arthur/i.test(v.name)) ??
        voices.find((v) => /en-GB/i.test(v.lang)) ??
        voices.find((v) => /^en/i.test(v.lang));
      if (pick) u.voice = pick;
      u.onend = done;
      u.onerror = done;
      synth.cancel();
      synth.speak(u);
      // A backstop, because some browsers never fire onend.
      setTimeout(() => {
        if (this.radioBusy) done();
      }, 3200);
    }, 220);
  }

  /** Water hitting fire: a bright burst falling away into a long hiss of steam. */
  steam(): void {
    const ctx = this.ctx;
    if (!ctx || !this.noise || !this.master) return;
    const t0 = ctx.currentTime;
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    src.playbackRate.value = 1.35;
    const band = ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.Q.value = 0.8;
    band.frequency.setValueAtTime(5200, t0);
    band.frequency.exponentialRampToValueAtTime(1300, t0 + 1.4);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.9, t0 + 0.05);
    g.gain.exponentialRampToValueAtTime(0.22, t0 + 0.55);
    g.gain.exponentialRampToValueAtTime(0.0008, t0 + 1.6);
    src.connect(band).connect(g).connect(this.master);
    src.start();
    src.stop(t0 + 1.7);
  }

  /**
   * A crowd cheering. Three things together make this sound like people rather
   * than like a swell of noise, and it needs all three:
   *   - noise shaped to the frequencies a mouth boosts when it says an open
   *     "ah" (its formants), which is what makes any noise sound like a voice
   *     at all;
   *   - a dozen voices at slightly different pitches, each wavering on its
   *     own, because the people in a crowd are never in tune with each other;
   *   - scattered claps over the top.
   */
  crowd(): void {
    const ctx = this.ctx;
    if (!ctx || !this.noise || !this.master) return;
    const t0 = ctx.currentTime;
    const dur = 5.4;
    const rnd = () => Math.random();

    // Shared swell: loud and sudden, not soft and slow. Very fast onset, held
    // wide open, then an uneven fade. That is the shape of a big crowd all
    // reacting at the same moment.
    const bus = ctx.createGain();
    bus.gain.setValueAtTime(0.0001, t0);
    // Builds rather than snapping on, because a crowd takes a moment to get
    // going, then holds for a good while before falling away unevenly.
    bus.gain.exponentialRampToValueAtTime(0.28, t0 + 0.55);
    bus.gain.linearRampToValueAtTime(0.64, t0 + 1.1);
    bus.gain.setValueAtTime(0.64, t0 + 3.1);
    bus.gain.exponentialRampToValueAtTime(0.0008, t0 + dur);
    bus.connect(this.master);

    // Vowel-shaped noise: the formants of "ah". Wide Q and a lot of gain,
    // because narrow formants look better on paper and cannot be heard in
    // practice.
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    src.loop = true;
    src.playbackRate.value = 0.9;
    // Each formant rises and falls in volume at its own slow rate. Steady
    // filtered noise is what sounds like static or wind; it is the constant
    // movement that makes a body of noise sound like a great many people.
    for (const [f, q, g, wobbleHz] of [
      [700, 2.2, 5.5, 0.9],
      [1150, 2.6, 3.8, 1.5],
      [2300, 3, 1.2, 2.3],
    ]) {
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = f;
      bp.Q.value = q;
      const trem = ctx.createGain();
      trem.gain.value = 0.62;
      const lfo = ctx.createOscillator();
      lfo.type = 'sine';
      lfo.frequency.value = wobbleHz;
      const lg = ctx.createGain();
      lg.gain.value = 0.34;
      lfo.connect(lg).connect(trem.gain);
      lfo.start(t0);
      lfo.stop(t0 + dur + 0.1);
      const fg = ctx.createGain();
      fg.gain.value = g;
      src.connect(bp).connect(trem).connect(fg).connect(bus);
    }
    src.start(t0);
    src.stop(t0 + dur + 0.1);

    // Individual shouts scattered through the roar: short glides at unrelated
    // pitches, each shaped by a formant so it reads as a voice rather than a
    // beep. Sustained oscillators in harmony are a synthesiser chord, which is
    // exactly what this sounded like before.
    for (let i = 0; i < 30; i++) {
      const at = t0 + 0.1 + rnd() * (dur - 1.3);
      const len = 0.16 + rnd() * 0.38;
      const f0 = 180 + rnd() * 430;
      const o = ctx.createOscillator();
      o.type = rnd() > 0.5 ? 'sawtooth' : 'triangle';
      o.frequency.setValueAtTime(f0 * (0.78 + rnd() * 0.2), at);
      o.frequency.exponentialRampToValueAtTime(f0 * (1.05 + rnd() * 0.3), at + len * 0.35);
      o.frequency.exponentialRampToValueAtTime(f0 * (0.72 + rnd() * 0.2), at + len);
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = 650 + rnd() * 800;
      bp.Q.value = 1.1;
      const og = ctx.createGain();
      og.gain.setValueAtTime(0.0001, at);
      og.gain.exponentialRampToValueAtTime(0.5 + rnd() * 0.6, at + 0.05);
      og.gain.exponentialRampToValueAtTime(0.0006, at + len);
      o.connect(bp).connect(og).connect(bus);
      o.start(at);
      o.stop(at + len + 0.05);
    }

    // Applause scattered across the whole cheer.
    for (let i = 0; i < 46; i++) {
      const at = t0 + 0.3 + rnd() * (dur - 1.4);
      const clap = ctx.createBufferSource();
      clap.buffer = this.noise;
      clap.playbackRate.value = 1.6 + rnd();
      const hp = ctx.createBiquadFilter();
      hp.type = 'highpass';
      hp.frequency.value = 1100 + rnd() * 1000;
      const cg = ctx.createGain();
      const cd = 0.018 + rnd() * 0.032;
      cg.gain.setValueAtTime(0.0001, at);
      cg.gain.exponentialRampToValueAtTime(0.09 + rnd() * 0.13, at + 0.003);
      cg.gain.exponentialRampToValueAtTime(0.0004, at + cd);
      clap.connect(hp).connect(cg).connect(this.master);
      clap.start(at);
      clap.stop(at + cd + 0.02);
    }
  }

  update(w: World): void {
    // While the title screen is up, its ambience controls the beds.
    if (this.titleMode) return;
    if (!this.ctx || !this.rotor || !this.fire || !this.wind || !this.fireFilter || !this.rotorChop) return;
    const t = this.ctx.currentTime;
    const h = w.heli;
    const speed = Math.hypot(h.vx, h.vy) / 105;
    const airborne = h.landed || h.grounded > 0 ? 0.3 : 1;
    this.rotor.gain.setTargetAtTime(TITLE_ROTOR * airborne * (0.75 + speed * 0.45), t, 0.12);
    // The faster the helicopter flies, the faster the blades beat.
    this.bladeHz = 8 + speed * 5;

    const fireLoud = clamp(w.fire.intensity / 260, 0, 1);
    this.fire.gain.setTargetAtTime(fireLoud * TITLE_FIRE * 1.05, t, 0.5);
    this.fireFilter.frequency.setTargetAtTime(380 + fireLoud * 620, t, 0.5);
    this.crackleRate = fireLoud * 20;
    this.crackleLevel = 0.34 + fireLoud * 0.3;
    this.wind.gain.setTargetAtTime(w.wind.speed * TITLE_WIND * 1.4, t, 0.6);
  }

  /** Short noise burst, for a water drop landing. */
  splash(): void {
    const ctx = this.ctx;
    if (!ctx || !this.noise || !this.master) return;
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    const band = ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.frequency.value = 1400;
    const g = ctx.createGain();
    g.gain.setValueAtTime(1.1, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.55);
    src.connect(band).connect(g).connect(this.master);
    src.start();
    src.stop(ctx.currentTime + 0.6);
  }

  /** Rising arpeggio when a load of people is delivered. */
  cheer(): void {
    this.arp([523, 659, 784, 1047], 0.09, 'triangle', 0.22);
  }

  /** Two-tone civil defence siren for the evacuation order. */
  alarm(): void {
    this.arp([740, 587, 740, 587], 0.28, 'square', 0.18);
  }

  /** Descending tones for bad news. */
  alert(): void {
    this.arp([392, 330], 0.16, 'square', 0.16);
  }

  // -------------------------------------------------------------------------
  // Arcade one-shots
  // -------------------------------------------------------------------------

  /** Short confirmation blip. */
  blip(): void {
    this.arp([880, 1320], 0.05, 'square', 0.15);
  }

  /** Two-note rise: an incident selected, an order accepted. */
  select(): void {
    this.arp([660, 880, 1320], 0.06, 'square', 0.17);
  }

  /** Flat buzz: an order refused. */
  deny(): void {
    this.arp([180, 140], 0.09, 'square', 0.16);
  }

  /** Two-tone siren: a fire engine arriving on scene. */
  siren(): void {
    this.arp([784, 587, 784, 587], 0.16, 'square', 0.13);
  }

  /** Urgent repeated note: a crew's escape route closing. */
  klaxon(): void {
    this.arp([932, 0, 932, 0, 932], 0.1, 'square', 0.19);
  }

  /** Falling tones: a crew lost. */
  toll(): void {
    this.arp([330, 262, 208, 165], 0.3, 'triangle', 0.2);
  }

  /** Air rushing: the bucket opening. */
  whoosh(): void {
    const ctx = this.ctx;
    if (!ctx || !this.noise || !this.master) return;
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    const band = ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.frequency.setValueAtTime(2200, ctx.currentTime);
    band.frequency.exponentialRampToValueAtTime(320, ctx.currentTime + 0.45);
    const g = ctx.createGain();
    g.gain.setValueAtTime(1.2, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
    src.connect(band).connect(g).connect(this.master);
    src.start();
    src.stop(ctx.currentTime + 0.55);
  }

  /** Rising fanfare on a pass. */
  jingleWin(): void {
    this.arp([523, 659, 784, 1047, 1319], 0.13, 'square', 0.2);
  }

  /** Falling notes on a fail. */
  jingleFail(): void {
    this.arp([392, 370, 349, 262], 0.22, 'square', 0.2);
  }

  // -------------------------------------------------------------------------
  // Title tune
  // -------------------------------------------------------------------------

  private tuneOn = false;
  private tuneAt = 0;
  private tuneTimer: ReturnType<typeof setTimeout> | null = null;

  setTitleTune(on: boolean): void {
    if (on === this.tuneOn) return;
    this.tuneOn = on;
    if (!on) {
      if (this.tuneTimer !== null) clearTimeout(this.tuneTimer);
      this.tuneTimer = null;
      return;
    }
    if (!this.ctx) return;
    this.tuneAt = this.ctx.currentTime + 0.15;
    this.pumpTune();
  }

  /** Schedule a couple of seconds ahead, then top up. */
  private pumpTune(): void {
    const ctx = this.ctx;
    if (!ctx || !this.tuneOn) return;
    while (this.tuneAt < ctx.currentTime + 2.5) this.tuneAt = this.scheduleBars(this.tuneAt);
    this.tuneTimer = setTimeout(() => this.pumpTune(), 700);
  }

  private scheduleBars(from: number): number {
    for (let i = 0; i < MELODY.length; i++) {
      const t = from + i * EIGHTH;
      this.tone(MELODY[i], t, EIGHTH * 0.9, 'square', 0.13);
      this.tone(HARMONY[i], t, EIGHTH * 0.85, 'square', 0.06);
      this.tone(BASS[i], t, EIGHTH * 0.8, 'triangle', 0.14);
      this.hit(DRUMS[i], t);
    }
    return from + MELODY.length * EIGHTH;
  }

  private tone(name: string, at: number, dur: number, type: OscillatorType, gain: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.master || name === '.') return;
    const f = freq(name);
    if (!f) return;
    const o = ctx.createOscillator();
    o.type = type;
    o.frequency.value = f;
    // Attack, sustain, quick release: a chiptune envelope. An exponential
    // decay across the whole note sounds like a pluck and leaves audible gaps.
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, at);
    g.gain.linearRampToValueAtTime(gain, at + 0.01);
    g.gain.setValueAtTime(gain, at + dur * 0.72);
    g.gain.exponentialRampToValueAtTime(0.0008, at + dur);
    o.connect(g).connect(this.master);
    o.start(at);
    o.stop(at + dur + 0.02);
  }

  private hit(kind: string, at: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.master || !this.noise || kind === '.') return;
    if (kind === 'K') {
      // Kick: a fast downward sine sweep.
      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.setValueAtTime(150, at);
      o.frequency.exponentialRampToValueAtTime(45, at + 0.1);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.24, at);
      g.gain.exponentialRampToValueAtTime(0.001, at + 0.13);
      o.connect(g).connect(this.master);
      o.start(at);
      o.stop(at + 0.15);
      return;
    }
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    src.playbackRate.value = kind === 'h' ? 1.9 : 1.1;
    const hp = ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = kind === 'h' ? 7000 : 1600;
    const g = ctx.createGain();
    const dur = kind === 'h' ? 0.035 : 0.11;
    g.gain.setValueAtTime(kind === 'h' ? 0.07 : 0.16, at);
    g.gain.exponentialRampToValueAtTime(0.0008, at + dur);
    src.connect(hp).connect(g).connect(this.master);
    src.start(at);
    src.stop(at + dur + 0.02);
  }

  private arp(freqs: number[], step: number, type: OscillatorType, gain: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    freqs.forEach((f, i) => {
      if (f <= 0) return; // a rest inside a pattern
      const o = ctx.createOscillator();
      o.type = type;
      o.frequency.value = f;
      const g = ctx.createGain();
      const t0 = ctx.currentTime + i * step;
      g.gain.setValueAtTime(0, t0);
      g.gain.linearRampToValueAtTime(gain, t0 + 0.012);
      g.gain.exponentialRampToValueAtTime(0.001, t0 + step * 0.95);
      o.connect(g).connect(this.master!);
      o.start(t0);
      o.stop(t0 + step);
    });
  }
}







